import { useEffect, useRef, useState } from 'react';
import { apiError } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { FileTypeIcon } from './FileTypeIcon';
import { downloadFile, useFilePreviewUrl } from '@/api/files';
import { useToast } from '@/hooks/useToast';

const TEXT_PREVIEW_LIMIT = 50_000; // chars

type PreviewKind = 'pdf' | 'image' | 'office' | 'text' | 'heic' | 'unsupported';

interface PreviewFile {
  id: string;
  name: string;
  mime_type?: string | null;
  size_bytes?: number;
}

interface Props {
  file: PreviewFile;
}

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'svgz']);
const TEXT_EXTS  = new Set(['txt', 'md', 'csv', 'json', 'log']);
const OFFICE_EXTS = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp']);
const HEIC_EXTS  = new Set(['heic', 'heif']);

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

function detectKind(file: PreviewFile): PreviewKind {
  const ext = extOf(file.name);
  if (ext === 'pdf') return 'pdf';
  if (HEIC_EXTS.has(ext)) return 'heic';
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (OFFICE_EXTS.has(ext)) return 'office';
  if (TEXT_EXTS.has(ext)) return 'text';

  const mime = (file.mime_type || '').toLowerCase();
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'image/heic' || mime === 'image/heif') return 'heic';
  if (mime.startsWith('image/')) return 'image';
  if (
    mime.includes('wordprocessingml') ||
    mime.includes('spreadsheetml') ||
    mime.includes('presentationml') ||
    mime.includes('msword') ||
    mime.includes('ms-excel') ||
    mime.includes('ms-powerpoint')
  ) return 'office';
  if (mime.startsWith('text/') || mime === 'application/json') return 'text';
  return 'unsupported';
}

export function FilePreview({ file }: Props) {
  const kind = detectKind(file);
  const toast = useToast();

  // PDFs are fetched as a blob from the presigned R2 URL and turned into a
  // local blob URL so the iframe never makes a cross-origin network request —
  // avoids the "refused to connect" browser security block for PDF iframes.
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  const needsPreviewUrl = kind === 'image' || kind === 'office' || kind === 'pdf' || kind === 'text';
  const previewUrlQuery = useFilePreviewUrl(needsPreviewUrl ? file.id : null);

  useEffect(() => {
    if (kind !== 'pdf') return;
    if (!previewUrlQuery.data?.url) return;
    setPdfBlobUrl(null);
    setPdfError(null);
    let cancelled = false;
    fetch(previewUrlQuery.data.url)
      .then((resp) => {
        if (!resp.ok) throw new Error(`Fetch failed (${resp.status})`);
        return resp.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setPdfBlobUrl(url);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPdfError(e instanceof Error ? e.message : 'Failed to load PDF');
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [file.id, kind, previewUrlQuery.data?.url]);

  const handleDownload = async () => {
    try {
      await downloadFile(file.id, file.name);
    } catch (e) {
      toast.error('Download failed', apiError(e));
    }
  };

  const renderBody = () => {
    if (kind === 'heic') {
      return (
        <div className="flex flex-col items-center justify-center text-center min-h-[400px] gap-3 text-slate-500">
          <FileTypeIcon name={file.name} size={48} />
          <div className="text-sm font-medium text-slate-600">HEIC preview not supported in this browser</div>
          <div className="text-xs text-slate-400 max-w-[280px]">
            HEIC is Apple's photo format. Download the file and open it on a Mac, iPhone, or with Apple Photos on Windows.
          </div>
          <Button variant="download" onClick={handleDownload} className="text-xs">
            Download
          </Button>
        </div>
      );
    }

    if (kind === 'unsupported') {
      return (
        <div className="flex flex-col items-center justify-center text-center min-h-[400px] gap-3 text-slate-500">
          <FileTypeIcon name={file.name} size={48} />
          <div className="text-sm">Preview not available — download to view.</div>
          <Button variant="download" onClick={handleDownload} className="text-xs">
            Download
          </Button>
        </div>
      );
    }

    if (kind === 'text') {
      if (previewUrlQuery.isLoading) {
        return (
          <div className="flex justify-center items-center min-h-[400px]">
            <Spinner size={28} />
          </div>
        );
      }
      if (previewUrlQuery.isError || !previewUrlQuery.data) {
        return (
          <div className="text-sm text-red-600 min-h-[400px] flex items-center justify-center">
            {apiError(previewUrlQuery.error) || 'Failed to load preview.'}
          </div>
        );
      }
      return <TextPreview presignedUrl={previewUrlQuery.data.url} fileName={file.name} />;
    }

    if (kind === 'pdf') {
      if (pdfError) {
        return (
          <div className="text-sm text-red-600 min-h-[400px] flex flex-col items-center justify-center gap-3">
            <p>{pdfError}</p>
            <Button variant="secondary" onClick={handleDownload} className="text-xs">Download instead</Button>
          </div>
        );
      }
      if (!pdfBlobUrl) {
        return (
          <div className="flex justify-center items-center min-h-[400px]">
            <Spinner size={28} />
          </div>
        );
      }
      return (
        <iframe
          src={pdfBlobUrl}
          className="w-full h-[700px] border-0"
          title={file.name}
        />
      );
    }

    if (previewUrlQuery.isLoading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <Spinner size={28} />
        </div>
      );
    }

    if (previewUrlQuery.isError || !previewUrlQuery.data) {
      return (
        <div className="text-sm text-red-600 min-h-[400px] flex items-center justify-center">
          {apiError(previewUrlQuery.error) || 'Failed to load preview.'}
        </div>
      );
    }

    const presignedUrl = previewUrlQuery.data.url;

    if (kind === 'image') {
      return (
        <div className="flex justify-center items-center bg-slate-50 border border-slate-200 min-h-[400px] p-3">
          <img src={presignedUrl} alt={file.name} className="max-w-full max-h-[600px] mx-auto" />
        </div>
      );
    }

    // Office files — use Microsoft Office Online viewer with the presigned R2 URL directly
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(presignedUrl)}`;

    return (
      <div className="flex flex-col gap-2">
        <iframe
          src={officeViewerUrl}
          className="w-full h-[700px] border border-slate-200"
          title={file.name}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
        <p className="text-xs text-slate-400 px-1">
          Previewed via Microsoft Office Online. If the document doesn't load,{' '}
          <button type="button" onClick={handleDownload} className="text-[#0410BD] hover:underline">
            download it instead
          </button>.
        </p>
      </div>
    );
  };

  return (
    <Card title="Preview" padded={kind !== 'pdf' && kind !== 'image' && kind !== 'office' && kind !== 'heic'}>
      {renderBody()}
    </Card>
  );
}

function TextPreview({ presignedUrl, fileName }: { presignedUrl: string; fileName: string }) {
  const [text, setText] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setText(null);
    setTruncated(false);

    fetch(presignedUrl)
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`Fetch failed (${resp.status})`);
        const raw = await resp.text();
        if (cancelled) return;
        if (raw.length > TEXT_PREVIEW_LIMIT) {
          setText(raw.slice(0, TEXT_PREVIEW_LIMIT));
          setTruncated(true);
        } else {
          setText(raw);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load file');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [presignedUrl]);

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Spinner size={28} /></div>;
  if (error) return <div className="text-sm text-red-600 min-h-[400px] flex items-center justify-center">{error}</div>;

  return (
    <div>
      <pre
        aria-label={`Text preview of ${fileName}`}
        className="whitespace-pre-wrap break-words text-xs font-mono text-slate-800 bg-slate-50 border border-slate-200 p-3 max-h-[600px] overflow-auto"
      >
        {text}
      </pre>
      {truncated && (
        <p className="text-xs text-slate-500 mt-2">
          (truncated — showing first {Math.round(TEXT_PREVIEW_LIMIT / 1000)} KB)
        </p>
      )}
    </div>
  );
}
